import React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart } from '../../../../types/Chart/Chart';
import useChart from '../../../../hooks/useChart';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { WebCamVideo } from '../../../../components/teleprompter/WebCamVideo';
import { NavForward } from '../../../../components/buttons/NavForward';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { isMobile } from 'react-device-detect';
import { Heading } from '../../../../components/Heading';
// import { useOrientation } from '@uidotdev/usehooks';

export const Route = createFileRoute('/_authenticated/$patientId/statement/recording')({
  component: VideoRecorder,
});

function VideoRecorder() {
  // const orientation = useOrientation();
  const navigate = useNavigate();
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [nextEnabled, setNextEnabled] = React.useState(false);
  const [warned, setWarned] = React.useState(false);

  // RELOAD THE PAGE ON ORIENTATION CHANGE
  // React.useEffect(() => {
  //   if (orientation?.type) {
  //     window.location.reload();
  //   }
  // }, [orientation]);

  // NAVIGATE TO ATTESTATION PAGE IF NOT ATTESTED
  React.useEffect(() => {
    if (chart?.SCRIPT_REVIEW.scriptAttested === false) {
      navigate({ to: `../../../${patientId}/statement/attestation` });
    }
  }, [chart, navigate, patientId]);

  // WARN MOBILE USERS THAT ENABLING CAMERA DISABLES AUDIO ON CONCIERGE CALL
  React.useEffect(() => {
    if (patientInfo && patientInfo.navType.base === 'concierge' && isMobile && !warned) {
      alert(
        'If you can no longer hear your concierge after using your camera, you may need to return to your Telehealth call and refresh your browser to reconnect with your clinician/concierge',
      );
      setWarned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'recording', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'recording',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="recording-page" className="flex w-full flex-col items-center justify-center">
          <Heading id="recording-page-header" textContent="Your MIDEO Safety Statement" />
          <div id="recording-page-instructions" className="flex flex-row">
            <WebCamVideo patientId={patientId} nextEnabled={nextEnabled} setNextEnabled={setNextEnabled} />
          </div>
          <div id="nav" className="mt-4 flex flex-row">
            {nextPageURL && nextEnabled && <NavForward to={nextPageURL} disabled={!nextEnabled} />}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
